import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API, formatApiErrorDetail, setToken } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, UserPlus, AlertTriangle, Lock } from "lucide-react";
import { toast } from "sonner";

/** Public claim landing for back-office team invites.
 *  Sent over WhatsApp by the org owner. Recipient sets credentials and
 *  is auto-signed-in into their assigned org role.
 */
export default function TeamJoin() {
  const { token } = useParams();
  const navigate  = useNavigate();
  const { refresh } = useAuth();

  const [invite, setInvite] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [err, setErr]       = useState(null);
  const [busy, setBusy]     = useState(false);

  const [name, setName]         = useState("");
  const [email, setEmail]       = useState("");
  const [phone, setPhone]       = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd]   = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/team-invites/${token}`);
        setInvite(data);
        if (data.invitee_name) setName(data.invitee_name);
        if (data.invitee_phone) setPhone(data.invitee_phone);
        if (data.organization?.logo_path) {
          try {
            const r = await api.get(`/files/${data.organization.logo_path}`, { responseType: "blob" });
            setLogoUrl(URL.createObjectURL(r.data));
          } catch (logoErr) {
            console.warn("[TeamJoin] logo fetch failed:", logoErr);   // best-effort
          }
        }
      } catch (e) {
        console.error("[TeamJoin] invite lookup failed:", e);
        setErr(formatApiErrorDetail(e.response?.data?.detail) || "This invite link is invalid or expired.");
      }
    })();
    return () => { if (logoUrl) URL.revokeObjectURL(logoUrl); };
    // eslint-disable-next-line
  }, [token]);

  const canSubmit = name.trim() && email.trim() && password.length >= 6 && !busy;

  const claim = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/team-invites/${token}/claim`, {
        full_name: name.trim(),
        email:     email.trim().toLowerCase(),
        password,
        phone:     phone.trim() || undefined,
      });
      setToken(data.access_token);
      await refresh?.();
      toast.success(`Welcome to ${data.organization.brand_name}`);
      navigate("/dashboard", { replace: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not claim invite");
    } finally {
      setBusy(false);
    }
  };

  if (err) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-6" data-testid="invite-error-state">
        <div className="cs-card rounded-2xl p-10 max-w-md text-center">
          <AlertTriangle size={32} className="mx-auto text-red-300 mb-3" />
          <h1 className="font-display text-2xl uppercase tracking-tight">Invite Unavailable</h1>
          <p className="mt-3 text-sm text-white/65">{err}</p>
        </div>
      </div>
    );
  }
  if (!invite) {
    return <div className="min-h-[60vh] flex items-center justify-center text-white/45 uppercase tracking-[0.3em] text-sm">Loading invite…</div>;
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" data-testid="team-join-page">
      <div className="w-full max-w-xl cs-card rounded-2xl p-7 sm:p-10 relative overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-px shimmer" />

        <div className="text-[10px] tracking-[0.3em] uppercase text-white/55 flex items-center gap-2">
          <Lock size={11} /> Private Team Invite
        </div>

        <div className="mt-5 flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center overflow-hidden shrink-0">
            {logoUrl
              ? <img src={logoUrl} alt="org logo" className="w-full h-full object-contain" />
              : <span className="font-display font-bold text-[14px] text-black">
                  {(invite.organization.brand_name || "TO").slice(0, 2).toUpperCase()}
                </span>}
          </div>
          <div className="min-w-0">
            <div className="text-[10px] tracking-[0.25em] uppercase text-white/45">Joining</div>
            <div className="font-display text-2xl uppercase tracking-tight truncate">
              {invite.organization.brand_name}
            </div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-white/55 mt-0.5">
              as <span className="text-[#F5B82E]">{invite.role_label}</span>
              {invite.engagement_type !== "staff" && (
                <> · <span className="text-white/75">{invite.engagement_type}</span></>
              )}
            </div>
          </div>
        </div>

        <div className="my-7 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />

        <div className="space-y-4">
          <div>
            <Label className="text-[10px] tracking-[0.25em] uppercase text-white/55">Full name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
                   className="mt-1.5 bg-black/30 border-white/15"
                   data-testid="claim-name-input" />
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.25em] uppercase text-white/55">Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                   placeholder="you@work.email"
                   className="mt-1.5 bg-black/30 border-white/15"
                   data-testid="claim-email-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] tracking-[0.25em] uppercase text-white/55">Phone</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)}
                     placeholder="+91 …"
                     className="mt-1.5 bg-black/30 border-white/15 font-mono"
                     data-testid="claim-phone-input" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.25em] uppercase text-white/55">Set password</Label>
              <div className="relative">
                <Input type={showPwd ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
                       placeholder="Min 6 chars"
                       className="mt-1.5 bg-black/30 border-white/15 pr-14"
                       data-testid="claim-password-input" />
                <button type="button" onClick={() => setShowPwd(!showPwd)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[9px] tracking-[0.2em] uppercase text-white/45 hover:text-white">
                  {showPwd ? "Hide" : "Show"}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-7 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-[9px] tracking-[0.22em] uppercase text-white/45 flex items-center gap-1.5">
            <ShieldCheck size={11} className="text-[#F5B82E]" />
            One-time setup · You can change details later
          </div>
          <Button onClick={claim} disabled={!canSubmit}
                  className="h-11 px-6 rounded-full text-[11px] uppercase tracking-[0.25em] btn-themed"
                  data-testid="claim-submit-btn">
            {busy ? "Joining…" : <><UserPlus size={14} className="mr-2" /> Join {invite.organization.brand_name}</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
